/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { notificationRunsService, type NotificationRun } from '../../services/notification-runs.service';
import { totalOf , NO_SORT, sortParams, type SortState} from '../../services/notification-engine.types';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import ProjectFilter from '../../components/common/project-filter';
import {
  CHANNEL_ICON, CHANNEL_LABEL, STATUS_TONE,
} from '../../components/notifications/notification-format';

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');

const statusSeverity = (s?: string | null) =>
  s === 'COMPLETE' ? 'success' : s === 'CANCELLED' ? 'danger' : s === 'ACTIVE' ? 'info' : 'secondary';

/**
 * Which channels this fire actually went out on, and how each fared.
 *
 * Read from the deliveries, not from the channels pinned in the snapshot: a
 * notification configured for three may have attempted one — a recipient with
 * no phone, a channel override, a suppression — and showing the configured set
 * would name a channel where nothing was ever sent.
 *
 * A delivery with no channel is one that never got that far (suppressed,
 * expired, unroutable). It is left out rather than shown as a channel, because
 * it is not one; the row's status already says the fire did not complete.
 */
const channelsOf = (r: NotificationRun) => {
  const byChannel = new Map<string, string[]>();
  (r.deliveries || []).forEach((d) => {
    if (!d.channel) return;
    byChannel.set(d.channel, [...(byChannel.get(d.channel) || []), d.status || '-']);
  });
  return [...byChannel.entries()].map(([channel, statuses]) => {
    const sent = statuses.filter((s) => s === 'SENT').length;
    return {
      channel,
      // The shared map, so a channel added there appears here too rather than
      // silently falling back to a generic dot. `pi-circle-fill` is the honest
      // fallback for one nobody has drawn yet: present, unnamed.
      icon: CHANNEL_ICON[channel] || 'pi-circle-fill',
      // Green only when every delivery on it arrived: one failure out of four is
      // not a channel that worked, and a green icon would stop anybody looking.
      tone: sent === statuses.length ? STATUS_TONE.SENT
        : sent > 0 ? STATUS_TONE.QUEUED : STATUS_TONE.FAILED,
      detail: `${CHANNEL_LABEL[channel] || channel}: ${sent} of ${statuses.length} sent`,
    };
  });
};

const NotificationRunList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToView } = useSecureNavigation({ entity: 'notification run', basePath: '/notification-runs' });
  const toast = useRef<Toast>(null);

  const [rows, setRows] = useState<NotificationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side sort: the table asks, the API orders. Sorting in the browser
  // would only order the rows already fetched, which is not what a sort control
  // appears to promise.
  const [sort, setSort] = useState<SortState>(NO_SORT);
  const [correlationId, setCorrelationId] = useState('');
  // '' = every project the caller may read, merged. Until now this list was not
  // project-scoped at all; the server scopes it and honours this narrowing.
  const [appFilter, setAppFilter] = useState('');

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);

  const fetchRows = useCallback(async (skip = 0, limit = pageSize, sorting: SortState = sort, correlation = correlationId, app = appFilter) => {
    try {
      setLoading(true);
      const resp = await notificationRunsService.list({
        skip, limit, ...sortParams(sorting),
        ...(correlation ? { correlationId: correlation } : {}),
        ...(app ? { appId: app } : {}),
      });
      setRows(resp.data || []);
      setTotal(totalOf(resp));
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load notification runs'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, correlationId, appFilter, showError]);

  // Mount fetch only: re-running on every fetchRows identity change would
  // reload the table on each page size change as well as on the page event.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRows(0, pageSize, NO_SORT, ''); }, []);

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(e.first, e.rows); };

  const handleSort = (e: any) => {
    const next: SortState = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(0, pageSize, next);
  };

  const columns: SaasColumn<NotificationRun>[] = useMemo(() => [
    {
      field: 'notificationCode', header: 'Notification',
      body: (r) => <span className="font-mono text-xs">{r.configuration?.notificationCode || r.configuration?.notificationMasterId || '-'}</span>,
    },
    {
      field: 'trigger', header: 'Trigger',
      body: (r) => (
        <span className="text-xs">
          {r.trigger?.source || '-'}
          {r.trigger?.eventType ? <span className="text-gray-400"> · {r.trigger.eventType}</span> : null}
        </span>
      ),
    },
    {
      field: 'channels', header: 'Channels',
      body: (r) => {
        const channels = channelsOf(r);
        if (!channels.length) return <span className="text-xs text-gray-400">-</span>;
        return (
          <div className="flex gap-2 flex-wrap items-center">
            {channels.map(({ channel, icon, tone, detail }) => (
              // The name is in the tooltip, not beside the icon: this column
              // answers "which channels, and did they work" at a glance, and
              // six words of text would make it a list to read instead.
              <i key={channel} className={`pi ${icon} text-sm ${tone}`}
                 title={detail} aria-label={detail} />
            ))}
          </div>
        );
      },
    },
    {
      field: 'executionStatus', header: 'Status', sortable: true,
      body: (r) => <Tag value={r.executionStatus || '-'} severity={statusSeverity(r.executionStatus)} className="text-xs px-1.5 py-0.5" />,
    },
    { field: 'createdAt', header: 'Fired at', sortable: true, body: (r) => <span className="text-xs">{fmt(r.createdAt)}</span> },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <i className="pi pi-eye text-green-600 dark:text-green-400" />
          </button>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PermissionGuard jobName={JOB_NAMES.NOTIFICATION_RUNS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-NotificationRun-List">
        {!embedded && (
          <PageHeader icon="pi pi-history" title="Notifications" subtitle="Every notification sent — who it reached, and every delivery attempt" testId="NotificationRun.Header" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="notification-run" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No notification runs found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="NotificationRunList"
            toolbarEnd={<>
              {/* Changing the project reloads from page 1: the row at offset 40
                  of one project is not the row at offset 40 of another. */}
              <ProjectFilter
                value={appFilter}
                onChange={(appId) => { setAppFilter(appId); setFirst(0); fetchRows(0, pageSize, sort, correlationId, appId); }}
                testId="NotificationRunList.ProjectFilter"
                minWidth={160}
              />
              <InputText
                value={correlationId}
                placeholder="Correlation id"
                className="w-48 text-sm"
                data-testid="NotificationRunList.Correlation"
                onChange={(e) => setCorrelationId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setFirst(0); fetchRows(0, pageSize, sort, correlationId); } }}
              />
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(first, pageSize)} loading={loading} testId="NotificationRunList.Refresh" />
            </>}
          />
        </div>
      </div>
    </PermissionGuard>
  );
};

export default NotificationRunList;
